/**
 * Alerts trends slice.
 *
 * Stores KPI, trend series, per-customer severity totals, and tooltip rows
 * for the alerts trends dashboard.
 */

import {
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";
import { ls } from "@/shared/utils/localStorage";
import type { AlertsTrendDataState, AlertsTrendItem, AlertsTrendsIntr } from "../types/alertsTrends.types";
import { alertsTrendService } from "../services/alertsTrendsService";

const ALERTS_TRENDS_FILTERS_KEY = "AlertsTrendsFilters";

const initialState: AlertsTrendDataState = {
  AlertsTrendDst: {
    kpi: null,
    trend: [],
    customerSeverity: [],
    customerSeverityTooltips: [],
  },
  _lastRefreshedTimeDst: null,
  status: {
    AlertsTrendDst: "idle",
  },
  filters: ls.get(ALERTS_TRENDS_FILTERS_KEY, {}),
  filtersInitialised: true,
};

let trendAbortController: AbortController | null = null;

/**
 * Fetches alerts trend payload and cancels stale in-flight requests.
 * @param payload Alerts trends request payload.
 * @returns API response or aborted rejection.
 */
export const fetchAlertsTrend = createAsyncThunk<
  AlertsTrendItem,
  AlertsTrendsIntr,
  { rejectValue: "aborted" }
>(
  "alertsTrendStore/fetchAlertsTrend",
  async (payload, { rejectWithValue }) => {
    try {
      if (trendAbortController) trendAbortController.abort();
      trendAbortController = new AbortController();

      const res = await alertsTrendService.GetAlertsTrend(payload, {
        signal: trendAbortController.signal,
      });

      return res;
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return rejectWithValue("aborted");
      }
      throw err;
    }
  },
);

const alertsTrendSlice = createSlice({
  name: "alert",
  initialState,
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
      ls.set(ALERTS_TRENDS_FILTERS_KEY, state.filters);
    },
    clearFilters(state) {
      state.filters = {};
      ls.set(ALERTS_TRENDS_FILTERS_KEY, state.filters);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlertsTrend.pending, (state) => {
        state.status.AlertsTrendDst = "loading";
      })
      .addCase(fetchAlertsTrend.fulfilled, (state, action) => {
        const res = action.payload;

        state.AlertsTrendDst.kpi = res?.kpiValues?.[0] ?? null;
        state.AlertsTrendDst.trend = res?.getAlertSeverityTrend ?? [];
        state.AlertsTrendDst.customerSeverity =
          res?.getCustomerSeverityCounts ?? [];
        state.AlertsTrendDst.customerSeverityTooltips =
          res?.getCustomerSeverityCountsTooltips ?? [];

        state.status.AlertsTrendDst = "succeeded";
      })
      .addCase(fetchAlertsTrend.rejected, (state, action) => {
        if (action.payload === "aborted") return;
        state.status.AlertsTrendDst = "failed";
      });
  },
});

/**
 * Selects alerts trend payload from Redux store.
 * @param state Root redux state.
 * @returns Alerts trend data state.
 */
export const selectAlertsTrend = (state: RootState) =>
  state.alertsTrend.AlertsTrendDst;
export const selectAlertsTrendStatus = (state: RootState) =>
  state.alertsTrend.status.AlertsTrendDst;

export const { setFilters, clearFilters } = alertsTrendSlice.actions;
export default alertsTrendSlice.reducer;
